import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Spinner from '@/components/Spinner';
import { toast } from 'sonner';
import { showError } from '../utils/errors';
import { Lock, Eye, EyeOff, ArrowRight, CheckCircle, AlertTriangle, CreditCard, LayoutDashboard, BookOpen, Coins, Network, Scale, HeartPulse, Mail, LogIn, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'https://api.trustoffice.app';

const WINGPOINT_PLAN = {
  name: 'WingPoint Annual',
  price: '$99/mo',
  billing: 'billed annually',
  annualTotal: '$1,188/year',
  trusts: 'Unlimited trusts',
  features: [
    'AI-powered guided minutes',
    '31 professional templates',
    'Minutes & Money integration',
    'Multi-trust dashboard',
    'Recurring task automation',
    'Governance calendar with reminders',
    'Distribution workflow with solvency checks',
    'Compensation tracker',
    'Governance Health Score',
    'Beneficiary management',
    'Professional PDF generation & data export',
    'Priority email support',
  ],
};

const FAQ_ITEMS = [
  {
    q: 'Why do I need a monthly subscription?',
    a: 'WingPoint created your trust. TrustOffice manages it: amendments, beneficiary updates, secure document storage, and access anytime. The monthly fee covers this ongoing service.',
  },
  {
    q: 'Is my trust ready?',
    a: 'Yes. Your trust was created when you purchased through WingPoint. TrustOffice is where you access and manage it.',
  },
  {
    q: 'What if I do not subscribe?',
    a: 'Your trust is legally created and valid. However, without an active TrustOffice subscription, you will not have access to manage it, make amendments, or retrieve documents through the platform.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Your TrustOffice subscription renews annually. If you cancel, you won\'t be able to get this special pricing again — you would need to subscribe at regular TrustOffice rates.',
  },
  {
    q: 'What happens if I cancel?',
    a: 'This exclusive WingPoint rate is available to WingPoint customers only. If you cancel, you won\'t be able to get this pricing again — you would need to subscribe at regular TrustOffice rates.',
  },
  {
    q: 'Why is this plan annual only?',
    a: 'WingPoint customers get unlimited trusts at a special rate below our public pricing. Annual commitment makes that possible.',
  },
  {
    q: 'My setup link expired',
    a: 'No problem. Email support@trustoffice.app and we will send you a new one right away.',
  },
  {
    q: 'Are WingPoint and TrustOffice the same company?',
    a: 'WingPoint and TrustOffice are separate services that work together. WingPoint creates your trust. TrustOffice manages it. Your purchase on WingPoint included access to this exclusive WingPoint plan.',
  },
];

export default function WingPointWelcomePage() {
  const { user, loading, subscription } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const action = searchParams.get('action');
  const token = searchParams.get('token');
  const coupon = searchParams.get('coupon');
  const plan = searchParams.get('plan');

  // Set password form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null = not checked, true/false = result
  const [passwordSet, setPasswordSet] = useState(false);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);

  // FAQ expand state
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Verify set-password token if present
  useEffect(() => {
    if (action === 'set_password' && token) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const verifyToken = async () => {
        try {
          const response = await fetch(
            `${API}/auth/verify-reset-token?token=${token}`,
            { signal: controller.signal }
          );
          clearTimeout(timeoutId);
          setTokenValid(response.ok);
        } catch (e) {
          clearTimeout(timeoutId);
          if (e.name === 'AbortError') {
            setTokenValid(false);
          } else {
            setTokenValid(false);
          }
        }
      };
      verifyToken();
      return () => {
        clearTimeout(timeoutId);
        controller.abort();
      };
    }
  }, [action, token]);

  // If user is logged in and we're showing set_password action, they probably already set it
  useEffect(() => {
    if (user && action === 'set_password') {
      // Redirect to the wingpoint page without the set_password params
      navigate('/wingpoint', { replace: true });
    }
  }, [user, action, navigate]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setSettingPassword(true);
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      if (!response.ok && !response.status === 400) {
        const data = await response.json().catch(() => ({}));
        if (data.detail.includes('"no password is set yet"')) {
          // UI: Redirect to reset flow with newaccount flag
          navigate("/forgot-password\?status=newaccount");
        } else {
          throw new Error('Failed to set password');
        }
      }
      setPasswordSet(true);
      toast.success('Password set successfully. Taking you to choose your plan...');
      // Redirect to pricing, preserving coupon/plan/wp params
      setTimeout(() => {
        const params = new URLSearchParams();
        params.set('wp', '1');
        params.set('action', 'subscribe');
        if (coupon) params.set('coupon', coupon);
        if (plan) params.set('plan', plan);
        params.set('billing', 'annual');
        navigate(`/pricing?${params.toString()}`, { replace: true });
      }, 2000);
    } catch (error) {
      showError(toast, error, { operation: 'set_password', page: 'WingPointWelcome' });
    } finally {
      setSettingPassword(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
      }
      // Reload the page so AuthContext picks up the new session
      window.location.reload();
    } catch (error) {
      showError(toast, error, { operation: 'wingpoint_login', page: 'WingPointWelcome' });
    } finally {
      setLoginLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-subtle-bg flex items-center justify-center">
        <div className="text-center">
          <Spinner size={32} className="mx-auto mb-4" />
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Loading</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-subtle-bg">
      {/* Top bar with TrustOffice logo */}
      <header className="bg-navy text-white py-4 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/assets/trustoffice-logo.svg?v=3" alt="TrustOffice" className="h-7" />
          </Link>
          {user && (
            <Link to="/dashboard" className="text-sm text-white/70 hover:text-white transition-colors">
              Dashboard
            </Link>
          )}
          {!user && (
            <Link to="/login" className="text-sm text-white/70 hover:text-white transition-colors">
              Log in
            </Link>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* 1. Welcome Section */}
        <section className="text-center mb-12">
          <div className="inline-block px-4 py-1.5 bg-gold/10 border border-gold/30 mb-6">
            <span className="text-sm font-medium text-navy">WingPoint Customer</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-navy mb-4 tracking-tight">
            Welcome to TrustOffice
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground mb-3 font-medium">
            The management platform for your WingPoint trust
          </p>
          <p className="text-base text-navy max-w-2xl mx-auto mb-4 font-medium">
            Your trust was created the moment you completed your WingPoint purchase. It is real, it is yours, and it is ready to access.
          </p>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto mb-6">
            WingPoint created your trust. TrustOffice keeps it running: updated, secure, and accessible whenever you need it.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/30">
            <CreditCard className="w-4 h-4 text-navy" />
            <span className="text-sm text-navy font-medium">
              You're eligible for WingPoint's exclusive annual plan at <span className="font-mono">$99/mo</span>.
            </span>
          </div>
        </section>

        {/* 1b. Payment Explanation Callout */}
        <section className="mb-12">
          <div className="max-w-3xl mx-auto bg-white border border-border p-6">
            <h3 className="text-base font-bold text-navy mb-3 text-center">
              How WingPoint and TrustOffice work together
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-navy">WingPoint created your trust</span> (one-time purchase, already complete).
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-navy">TrustOffice manages it</span> (monthly service: amendments, documents, secure access).
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-navy">WingPoint Annual plan</span> provides unlimited trusts at <span className="font-mono">$99/mo</span>, billed annually.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 2. What TrustOffice Does (with screenshot) */}
        <section className="mb-12">
          <h2 className="text-xl sm:text-2xl font-serif font-bold text-navy text-center mb-2">
            What TrustOffice does for you
          </h2>
          <p className="text-sm text-muted-foreground text-center mb-8">
            Your TrustOffice dashboard is where you manage everything about your trust. Here is what you will see when you log in.
          </p>

          {/* Dashboard screenshot */}
          <div className="max-w-4xl mx-auto mb-8 border border-border shadow-lg">
            <img
              src="/assets/dashboard-hero.png"
              alt="TrustOffice dashboard showing trust management features including Governance, Money, Structure, Compliance, and Trust Health"
              className="w-full"
            />
          </div>

          {/* Feature grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            <div className="bg-white border border-border p-5">
              <div className="flex items-center gap-2 mb-2">
                <LayoutDashboard className="w-5 h-5 text-gold" />
                <h4 className="text-sm font-bold text-navy">Dashboard</h4>
              </div>
              <p className="text-xs text-muted-foreground">See your trust status at a glance. Getting Started checklist guides you through setup step by step.</p>
            </div>
            <div className="bg-white border border-border p-5">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-5 h-5 text-gold" />
                <h4 className="text-sm font-bold text-navy">Governance</h4>
              </div>
              <p className="text-xs text-muted-foreground">Record trustee meetings, pass resolutions, and maintain the paper trail that keeps your trust legally defensible.</p>
            </div>
            <div className="bg-white border border-border p-5">
              <div className="flex items-center gap-2 mb-2">
                <Coins className="w-5 h-5 text-gold" />
                <h4 className="text-sm font-bold text-navy">Money</h4>
              </div>
              <p className="text-xs text-muted-foreground">Track trust accounts, record contributions and distributions, and keep finances organized in one place.</p>
            </div>
            <div className="bg-white border border-border p-5">
              <div className="flex items-center gap-2 mb-2">
                <Network className="w-5 h-5 text-gold" />
                <h4 className="text-sm font-bold text-navy">Structure</h4>
              </div>
              <p className="text-xs text-muted-foreground">Manage beneficiaries, trustees, and trust structure. Update roles and relationships as circumstances change.</p>
            </div>
            <div className="bg-white border border-border p-5">
              <div className="flex items-center gap-2 mb-2">
                <Scale className="w-5 h-5 text-gold" />
                <h4 className="text-sm font-bold text-navy">Compliance</h4>
              </div>
              <p className="text-xs text-muted-foreground">Risk dashboard and state compliance tracking. Know what is required and stay ahead of deadlines.</p>
            </div>
            <div className="bg-white border border-border p-5">
              <div className="flex items-center gap-2 mb-2">
                <HeartPulse className="w-5 h-5 text-gold" />
                <h4 className="text-sm font-bold text-navy">Trust Health</h4>
              </div>
              <p className="text-xs text-muted-foreground">A score that tells you how well your records would hold up if anyone ever asked. Know your weak spots before they become problems.</p>
            </div>
          </div>
        </section>

        {/* 3. Package Recognition Section */}
        <section className="mb-12">
          <h2 className="text-xl sm:text-2xl font-serif font-bold text-navy text-center mb-2">
            Your exclusive WingPoint plan
          </h2>
          <p className="text-sm text-muted-foreground text-center mb-8">
            WingPoint customers receive special access to this unlimited annual plan.
          </p>
          <div className="max-w-2xl mx-auto">
            <div className="bg-white border border-border p-6 flex flex-col">
              <h3 className="text-lg font-bold text-navy mb-1">{WINGPOINT_PLAN.name}</h3>
              <p className="text-2xl font-mono font-bold text-navy mb-1">{WINGPOINT_PLAN.price}</p>
              <p className="text-sm text-muted-foreground mb-4"><span className="font-mono">{WINGPOINT_PLAN.annualTotal}</span> ({WINGPOINT_PLAN.billing})</p>
              <p className="text-sm text-navy mb-4 font-medium">{WINGPOINT_PLAN.trusts}</p>
              <ul className="space-y-2 flex-1">
                {WINGPOINT_PLAN.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 3. Smart Action Section */}
        <section className="mb-12">
          <div className="bg-white border border-border p-6 sm:p-8">
            {!user ? (
              <NotLoggedInAction
                action={action}
                token={token}
                tokenValid={tokenValid}
                password={password}
                setPassword={setPassword}
                confirmPassword={confirmPassword}
                setConfirmPassword={setConfirmPassword}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                handleSetPassword={handleSetPassword}
                settingPassword={settingPassword}
                passwordSet={passwordSet}
                showLoginForm={showLoginForm}
                setShowLoginForm={setShowLoginForm}
                loginEmail={loginEmail}
                setLoginEmail={setLoginEmail}
                loginPassword={loginPassword}
                setLoginPassword={setLoginPassword}
                handleLogin={handleLogin}
                loginLoading={loginLoading}
                navigate={navigate}
              />
            ) : (
              <LoggedInAction
                subscription={subscription}
                navigate={navigate}
                plan={searchParams.get('plan')}
              />
            )}
          </div>
        </section>

        {/* 3b. Contact Section */}
        <section className="mb-12">
          <div className="max-w-3xl mx-auto text-center bg-white border border-border p-6">
            <h3 className="text-base font-bold text-navy mb-2">
              Questions?
            </h3>
            <p className="text-sm text-muted-foreground">
              Email <a href="mailto:support@trustoffice.app" className="font-medium text-navy underline">support@trustoffice.app</a> or reply to your welcome email. We respond within one business day.
            </p>
          </div>
        </section>

        {/* 4. FAQ Section */}
        <section className="mb-12">
          <h2 className="text-xl sm:text-2xl font-serif font-bold text-navy text-center mb-6">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {FAQ_ITEMS.map((item, idx) => (
              <div
                key={idx}
                className="bg-white border border-border overflow-hidden"
              >
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-subtle-bg/50 transition-colors"
                >
                  <span className="text-sm sm:text-base font-medium text-navy pr-4">{item.q}</span>
                  {expandedFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  )}
                </Button>
                {expandedFaq === idx && (
                  <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Footer link back to WingPoint */}
        <div className="text-center pb-8">
          <a
            href="https://wingpointtrust.com"
            className="text-sm text-muted-foreground hover:text-navy transition-colors"
          >
            wingpointtrust.com
          </a>
        </div>
      </div>
    </div>
  );
}

/* ---- Sub-components ---- */

function NotLoggedInAction({
  action, token, tokenValid,
  password, setPassword,
  confirmPassword, setConfirmPassword,
  showPassword, setShowPassword,
  handleSetPassword, settingPassword, passwordSet,
  showLoginForm, setShowLoginForm,
  loginEmail, setLoginEmail,
  loginPassword, setLoginPassword,
  handleLogin, loginLoading,
  navigate,
}) {
  // Case: set_password action with token (from email link)
  if (action === 'set_password' && token) {
    return (
      <div>
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-6 text-xs sm:text-sm">
          <div className="flex items-center gap-2">
            <span className="font-bold text-navy">Step 1: Set your password</span>
          </div>
          <span className="text-muted-foreground">{'> '}</span>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Step 2: Choose your plan</span>
          </div>
          <span className="text-muted-foreground">{'> '}</span>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Step 3: Access your trust</span>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gold/10 border border-gold/30 flex items-center justify-center">
            <Lock className="w-5 h-5 text-navy" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-navy">Set Your Password</h3>
            <p className="text-sm text-muted-foreground">Create a password to access your TrustOffice account.</p>
          </div>
        </div>

        {tokenValid === null && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner size={16} />
            Verifying your link...
          </div>
        )}

        {tokenValid === false && (
          <div className="p-4 bg-red-50 border border-red-200">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Your link has expired.</p>
                <p className="text-sm text-red-700 mt-1">
                  Please request a new connection link from WingPoint.
                </p>
              </div>
            </div>
          </div>
        )}

        {tokenValid === true && !passwordSet && (
          <form onSubmit={handleSetPassword} className="space-y-4 max-w-md">
            <div>
              <Label htmlFor="password">New Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy"
                aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
                className="mt-1"
              />
            </div>
            <Button type="submit" disabled={settingPassword} className="w-full">
              {settingPassword ? 'Setting password...' : 'Set Password'}
            </Button>
          </form>
        )}

        {passwordSet && (
          <div className="p-4 bg-green-50 border border-green-200">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-900">Password set!</p>
                <p className="text-sm text-green-700 mt-1">Taking you to choose your plan...</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Case: subscribe action (WingPoint redirect asking to log in and subscribe)
  if (action === 'subscribe') {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gold/10 border border-gold/30 flex items-center justify-center">
            <LogIn className="w-5 h-5 text-navy" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-navy">Log in to set up your account</h3>
            <p className="text-sm text-muted-foreground">Your trust is ready. Log in to activate your management plan.</p>
          </div>
        </div>
        <InlineLoginForm
          loginEmail={loginEmail}
          setLoginEmail={setLoginEmail}
          loginPassword={loginPassword}
          setLoginPassword={setLoginPassword}
          handleLogin={handleLogin}
          loginLoading={loginLoading}
          showLoginForm={showLoginForm}
          setShowLoginForm={setShowLoginForm}
        />
      </div>
    );
  }

  // Default: visiting from WingPoint button, no specific action
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-navy mb-4">Get started with TrustOffice</h3>

        {/* Path 1: New customer */}
        <div className="p-5 bg-gold/10 border border-gold/30 mb-4">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-navy flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-navy mb-1">New here? Check your email for setup instructions.</p>
              <p className="text-sm text-muted-foreground">
                We sent you an email with a link to set your password. Click the link in that email to create your account.
              </p>
            </div>
          </div>
        </div>

        {/* Path 2: Already have account */}
        <div className="p-5 border border-border">
          <div className="flex items-start gap-3">
            <LogIn className="w-5 h-5 text-navy flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-navy mb-1">Already have an account? Log in.</p>
              <p className="text-sm text-muted-foreground mb-4">
                If you have already set your password, log in to manage your trust and choose your plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => navigate('/login?wp=1&action=subscribe&plan=wingpoint&billing=annual')}
                  className="w-full sm:w-auto"
                >
                  Log In
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowLoginForm(!showLoginForm)}
                  className="w-full sm:w-auto"
                >
                  {showLoginForm ? 'Hide form' : 'Log in here'}
                </Button>
              </div>
              {showLoginForm && (
                <div className="mt-4">
                  <InlineLoginForm
                    loginEmail={loginEmail}
                    setLoginEmail={setLoginEmail}
                    loginPassword={loginPassword}
                    setLoginPassword={setLoginPassword}
                    handleLogin={handleLogin}
                    loginLoading={loginLoading}
                    showLoginForm={showLoginForm}
                    setShowLoginForm={setShowLoginForm}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InlineLoginForm({ loginEmail, setLoginEmail, loginPassword, setLoginPassword, handleLogin, loginLoading }) {
  return (
    <form onSubmit={handleLogin} className="space-y-4 max-w-md">
      <div>
        <Label htmlFor="loginEmail">Email</Label>
        <Input
          id="loginEmail"
          type="email"
          value={loginEmail}
          onChange={(e) => setLoginEmail(e.target.value)}
          placeholder="you@example.com"
          required
          className="mt-1"
        />
      </div>
      <div>
        <Label htmlFor="loginPassword">Password</Label>
        <Input
          id="loginPassword"
          type="password"
          value={loginPassword}
          onChange={(e) => setLoginPassword(e.target.value)}
          placeholder="Your password"
          required
          className="mt-1"
        />
      </div>
      <Button type="submit" disabled={loginLoading} className="w-full">
        {loginLoading ? 'Logging in...' : 'Log In'}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        <Link to="/forgot-password" className="underline">Forgot your password?</Link>
      </p>
    </form>
  );
}

function LoggedInAction({ subscription, navigate, plan }) {
  const hasActiveSubscription = subscription?.is_active;
  const needsUpgrade = subscription?.needs_upgrade;
  const status = subscription?.status;
  const isPastDue = status === 'past_due';
  const planParam = plan || 'wingpoint';

  // Past due: need payment update
  if (hasActiveSubscription && isPastDue) {
    return (
      <ActionCard
        icon={<AlertTriangle className="w-5 h-5 text-navy" />}
        title="Update your payment method"
        description="Your subscription payment failed. Update your payment method to restore full access to your trust management tools."
        buttonText="Update Payment Method"
        onClick={() => navigate('/settings/billing?wp=1&action=update_payment')}
        buttonVariant="default"
      />
    );
  }

  // Needs upgrade — forever_free is a permanent free tier, never show upgrade nudges
  if (hasActiveSubscription && needsUpgrade && subscription?.plan_type !== 'forever_free') {
    return (
      <ActionCard
        icon={<AlertTriangle className="w-5 h-5 text-navy" />}
        title="Upgrade your plan"
        description="You have more trusts than your current plan allows. Upgrade to continue managing all your trusts without interruption."
        buttonText="Upgrade Your Plan"
        onClick={() => navigate('/settings/billing?wp=1&action=upgrade')}
        buttonVariant="default"
      />
    );
  }

  // No active subscription
  if (!hasActiveSubscription) {
    return (
      <div className="space-y-4">
        <ActionCard
          icon={<CreditCard className="w-5 h-5 text-navy" />}
          title="Activate your WingPoint plan"
          description={<>Your trust is ready. Activate your exclusive WingPoint Annual plan with unlimited trusts at <span className="font-mono">$99/mo</span>, billed annually.</>}
          buttonText="Start WingPoint Plan"
          onClick={() => navigate(`/pricing?wp=1&plan=wingpoint&billing=annual`)}
          buttonVariant="default"
        />
        <div className="p-4 bg-gold/10 border border-gold/30">
          <p className="text-sm text-navy">
            <strong>WingPoint Exclusive:</strong> Unlimited trusts for <span className="font-mono">$99/mo</span> (billed annually). This special rate is available to WingPoint customers only and is not listed on our public pricing page.
          </p>
        </div>
      </div>
    );
  }

  // All set: subscription active, no upgrade needed
  return (
    <ActionCard
      icon={<LayoutDashboard className="w-5 h-5 text-navy" />}
      title="You are all set!"
      description="Your TrustOffice account is active and your trust is ready to manage. Head to your dashboard to get started."
      buttonText="Go to Your Dashboard"
      onClick={() => navigate('/dashboard')}
      buttonVariant="default"
    />
  );
}

function ActionCard({ icon, title, description, buttonText, onClick, buttonVariant = 'default' }) {
  return (
    <div className="flex flex-col sm:flex-row items-start gap-4">
      <div className="w-12 h-12 bg-gold/10 border border-gold/30 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-bold text-navy mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground mb-4">{description}</p>
        <Button onClick={onClick} variant={buttonVariant} className="w-full sm:w-auto">
          {buttonText}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
